import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, TrendingUp, Users, Store, Sparkles, GraduationCap, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import Waitlist from "@/components/Waitlist";
import StoryTeaser from "@/components/StoryTeaser";

const HERO = "https://images.pexels.com/photos/9146943/pexels-photo-9146943.jpeg";
const SHOP = "https://images.pexels.com/photos/18702143/pexels-photo-18702143.jpeg";
const CUT = "https://images.pexels.com/photos/3998407/pexels-photo-3998407.jpeg";
const RESERVE = "https://static.prod-images.emergentagent.com/jobs/f2933716-8a33-4553-905d-fbbd725a7c18/images/1533cb96b6f2b5a6bdcf7452ed1d22d1debc5060c4aa5728976f804e1e95a9ed.jpeg";

const VALUE = [
  { icon: Users, title: "Customers find quality", text: "Serious barbers, verified craft, real trust markers." },
  { icon: TrendingUp, title: "Barbers get noticed", text: "Status, leads, education, and exposure that grows books." },
  { icon: Store, title: "Shops grow faster", text: "Team visibility, recruitment, and featured shop drops." },
  { icon: Sparkles, title: "Vendors reach the audience", text: "Product drops and campaigns in front of real barbers." },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 hero-grid-bg opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-32 md:py-48">
          <motion.div initial="hidden" animate="show" variants={fade} custom={0}
            className="inline-flex items-center gap-2 border border-[#D4AF37]/40 px-3 py-1.5 mb-8">
            <ShieldCheck size={14} className="text-[#D4AF37]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#D4AF37]">The Barber Quality Network</span>
          </motion.div>

          <motion.h1 initial="hidden" animate="show" variants={fade} custom={1}
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-4xl">
            Where serious barbers<br />become <span className="gold-text gold-glow-text">certified</span>,<br />seen, and paid.
          </motion.h1>

          <motion.p initial="hidden" animate="show" variants={fade} custom={2}
            className="mt-7 text-base md:text-lg text-neutral-300 max-w-xl leading-relaxed">
            Barbistry isn't a directory. It's the trusted network where customers find quality, barbers earn status and leads, and shops and vendors pay to be seen.
          </motion.p>

          <motion.div initial="hidden" animate="show" variants={fade} custom={3}
            className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/register" data-testid="hero-join-btn">
              <Button className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-12 px-8 text-base group">
                Join Barbistry <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to="/directory" data-testid="hero-directory-btn">
              <Button variant="outline" className="rounded-none border-white/25 bg-transparent text-white hover:border-[#D4AF37] hover:text-[#D4AF37] h-12 px-8 text-base">
                Find a Barber
              </Button>
            </Link>
          </motion.div>

          <motion.div initial="hidden" animate="show" variants={fade} custom={4}
            className="mt-16 flex flex-wrap gap-x-12 gap-y-4 text-sm text-neutral-400 font-mono">
            <span><span className="gold-text text-lg font-semibold">7</span> membership tiers</span>
            <span><span className="gold-text text-lg font-semibold">$49.99</span> Quality Certified</span>
            <span><span className="gold-text text-lg font-semibold">70–80%</span> educator payout</span>
          </motion.div>
        </div>
      </section>

      {/* BRAND MANIFESTO */}
      <section className="relative z-10 border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37] mb-5">The future of barbering</p>
          <h2 className="font-display text-2xl md:text-4xl font-semibold leading-snug">Conscious, creative craftsmanship —<br className="hidden sm:block" /> <span className="gold-text">building a world of gentlemen.</span></h2>
        </div>
      </section>

      {/* BARBER JOURNAL — stories moved to the top with a 2-second teaser gate */}
      <StoryTeaser />

      <Waitlist />

      {/* VALUE PROP */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-4">The value proposition</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold">Simple. Everyone wins.</h2>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUE.map((v, i) => (
            <motion.div key={v.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
              className="group bg-[#111] border border-white/10 p-7 transition-transform duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/40">
              <v.icon className="text-[#D4AF37]" size={26} />
              <h3 className="mt-5 font-display text-lg font-semibold">{v.title}</h3>
              <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{v.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CERTIFICATION BANNER */}
      <section className="relative z-10 border-y border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={CUT} alt="Certified barber at work" className="w-full h-[420px] object-cover border border-white/10" />
            <div className="absolute -bottom-5 -right-3 sm:right-8 bg-[#D4AF37] text-black px-5 py-4 gold-glow">
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} />
                <div>
                  <p className="font-display font-bold leading-none">Quality Certified</p>
                  <p className="text-xs font-mono">Trust marker that pays for itself</p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-4">Certification</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
              One haircut covers the membership.
            </h2>
            <p className="mt-5 text-neutral-300 leading-relaxed">
              The Barbistry Quality Certified badge is a public trust marker: better local ranking, private client messages, lead access, and the positioning to charge more. Get even one or two extra quality clients and it pays for itself.
            </p>
            <Link to="/certification" className="inline-block mt-7">
              <Button variant="outline" className="rounded-none border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 bg-transparent" data-testid="home-certification-btn">
                How certification works <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* PATHS */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-4">Built for the whole industry</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold">Pick your path.</h2>
        </div>
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {[
            { icon: TrendingUp, t: "Barbers", d: "Join Insider, get Certified, build a name. Leads, education, and spotlight opportunities.", to: "/pricing" },
            { icon: Store, t: "Shops & Vendors", d: "Team visibility, recruitment, featured drops, product exposure, sponsored campaigns.", to: "/advertising" },
            { icon: GraduationCap, t: "Educators", d: "Host paid classes, sell tickets, build authority. Keep 70–80% of ticket sales.", to: "/events" },
          ].map((p, i) => (
            <motion.div key={p.t} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}>
              <Link to={p.to} className="block h-full bg-[#111] border border-white/10 p-8 group transition-transform duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/40" data-testid={`home-path-${p.t.toLowerCase().split(" ")[0]}`}>
                <p.icon className="text-[#D4AF37]" size={28} />
                <h3 className="mt-6 font-display text-2xl font-semibold">{p.t}</h3>
                <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{p.d}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm text-[#D4AF37] group-hover:gap-3 transition-all">Explore <ArrowRight size={15} /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BARBISTRY RESERVE TEASER */}
      <section className="relative z-10 overflow-hidden border-y border-white/10">
        <img src={RESERVE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/50" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28">
          <div className="max-w-xl">
            <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-4 flex items-center gap-2"><Leaf size={14} /> Coming Soon</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight">Barbistry Reserve.</h2>
            <p className="mt-5 text-neutral-300 leading-relaxed">
              High-end apparel and gear for the modern gentleman — cut from organic materials, made for the craft. Limited drops. Join the list for first access.
            </p>
            <Link to="/reserve" className="inline-block mt-8">
              <Button className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-12 px-8 text-base group" data-testid="home-reserve-btn">
                Preview the collection <ArrowRight size={17} className="ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 overflow-hidden">
        <img src={SHOP} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-28 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-bold">Quality gets noticed here.</h2>
          <p className="mt-5 text-neutral-300 max-w-xl mx-auto">Join the network that turns craft into status, leads, and income.</p>
          <Link to="/register" className="inline-block mt-9">
            <Button className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-12 px-10 text-base" data-testid="home-cta-join">
              Get Started
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
